import { useState } from "react";
import Logomark from "@/components/Logomark";
import PublicLayout from "@/components/PublicLayout";
import AtsExplainer from "@/components/public/AtsExplainer";
import { Link } from "react-router-dom";
import { Loader2, Lock, ArrowRight, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { IconRise } from "@/components/landing/icons";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// Matches routers/public_scan.MIN_CHARS. Checked here so the user gets the
// message inline rather than a 422 round-trip.
const MIN_CHARS = 50;

function scoreTone(score) {
  if (score >= 80) return "text-emerald-700 border-emerald-200 bg-emerald-50";
  if (score >= 50) return "text-amber-700 border-amber-200 bg-amber-50";
  return "text-rose-700 border-rose-200 bg-rose-50";
}

/**
 * Free, no-signup ATS scan — the pre-signup front door.
 *
 * Every other AI surface requires an account, so a visitor had no way to see
 * whether the product works before committing. This gives away the score and
 * a few gaps; the full keyword list, the rewrite recommendations, tailoring
 * and tracking need an account.
 *
 * The withheld counts come from the server rather than being inferred here,
 * so the page can say exactly what signing up buys instead of implying it.
 */
export default function FreeScan() {
  const { user } = useAuth();
  // Handed over from the landing hero. Session storage, not a query string:
  // a resume in a URL ends up in history, server logs and analytics paths.
  const [resume, setResume] = useState(() => {
    try {
      const handed = sessionStorage.getItem("nextopp_scan_resume");
      if (handed) sessionStorage.removeItem("nextopp_scan_resume");
      return handed || "";
    } catch {
      return "";
    }
  });
  const [jd, setJd] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // 503 means "not today, but signing in works right now" — a different exit
  // from a generic failure, so the CTA differs too.
  const [atCapacity, setAtCapacity] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setAtCapacity(false);
    if (resume.trim().length < MIN_CHARS || jd.trim().length < MIN_CHARS) {
      setError(`Paste at least ${MIN_CHARS} characters of both your resume and the job description.`);
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/public/ats-scan", {
        resume_text: resume, job_description: jd,
      });
      setResult(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setError("That’s a few scans in a short time — wait a minute and try again, or create a free account for unlimited scans.");
      } else if (status === 503) {
        setAtCapacity(true);
        setError(detail || "Free scans are at capacity for today.");
      } else {
        setError(detail || "Something went wrong running that scan. Please try again.");
      }
    } finally {
      // Always re-enabled: a failure the user cannot retry is a dead end.
      setLoading(false);
    }
  };

  return (
    <PublicLayout>

      {/* Two columns at every stage. The form used the left half of a
          max-w-5xl column and the right half of the screen was empty until a
          result arrived — on the page the entire landing hero funnels into.
          The rail now previews the shape of the answer before you scan and
          becomes the answer afterwards, so nothing is ever dead space. */}
      <section className="max-w-7xl mx-auto px-6 py-10 lg:py-14">
        <div className="max-w-3xl">
          <div className="u-label text-stone-600">Free ATS scan · no account</div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            Free ATS resume checker — score your resume against any{" "}
            <em className="font-display not-italic text-dawn-800">job</em>.
          </h1>
          <p className="mt-3 text-sm text-stone-600 max-w-2xl">
            Paste your resume and the job description. No account, no card. We never
            invent experience you don’t have — the scan only reports what’s actually
            in your resume.
          </p>
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          <form onSubmit={submit} className="lg:col-span-7 space-y-4">
            <div>
              <Label htmlFor="scan-resume" className="text-sm font-medium mb-1.5 block">Your resume</Label>
              <Textarea
                id="scan-resume" rows={11} value={resume}
                onChange={(e) => setResume(e.target.value)}
                placeholder="Paste the full text of your resume…"
                className="rounded-sm font-mono text-xs bg-white" data-testid="scan-resume"
              />
            </div>
            <div>
              <Label htmlFor="scan-jd" className="text-sm font-medium mb-1.5 block">The job description</Label>
              <Textarea
                id="scan-jd" rows={8} value={jd}
                onChange={(e) => setJd(e.target.value)}
                placeholder="Paste the job posting…"
                className="rounded-sm font-mono text-xs bg-white" data-testid="scan-jd"
              />
            </div>

            {error && (
              <div role="alert" className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-sm px-4 py-3" data-testid="scan-error">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    {error}
                    {atCapacity && (
                      <Link to="/signup" className="block mt-2 font-medium underline" data-testid="scan-error-signup">
                        Create a free account and scan now
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4">
              <Button type="submit" disabled={loading}
                      className="h-11 px-6 rounded-sm bg-brand hover:bg-brand-hover text-white font-medium"
                      data-testid="scan-submit">
                {loading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
                  : "Scan my resume"}
              </Button>
              <span className="text-xs text-stone-600">Nothing is stored unless you make an account.</span>
            </div>
          </form>

          <aside className="lg:col-span-5 lg:sticky lg:top-6">
            {result ? (
              <div className="border border-sand rounded-sm bg-white overflow-hidden" data-testid="scan-result">
                <div className="p-5 border-b border-sand flex items-center gap-5">
                  <div className={`w-20 h-20 shrink-0 grid place-items-center rounded-sm border-2 ${scoreTone(result.match_score)}`}>
                    <div className="font-heading text-3xl font-bold" data-testid="scan-score">{result.match_score}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="u-label text-stone-600">Match score</div>
                    <div className="font-heading text-xl font-bold tracking-tight capitalize">{result.verdict} match</div>
                    {result.seniority_fit && (
                      <div className="text-sm text-stone-600 mt-0.5">Seniority: {result.seniority_fit}</div>
                    )}
                  </div>
                </div>

                <div className="p-5 grid sm:grid-cols-2 gap-5">
                  <div>
                    <div className="u-label text-stone-600 mb-2">Already matching</div>
                    <ul className="space-y-1 text-sm">
                      {(result.matching_keywords || []).map((k) => (
                        <li key={k} className="text-emerald-800">{k}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="u-label text-stone-600 mb-2">Missing</div>
                    <ul className="space-y-1 text-sm">
                      {(result.missing_keywords || []).map((k) => (
                        <li key={k} className="text-rose-800">{k}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="p-5 border-t border-sand bg-paper" data-testid="scan-withheld">
                  <div className="flex items-start gap-3">
                    <Lock className="w-4 h-4 mt-0.5 text-stone-600 shrink-0" aria-hidden="true" />
                    <div>
                      <div className="font-heading font-bold text-base tracking-tight">
                        {result.missing_keywords_withheld} more gaps and{" "}
                        {result.recommendations_withheld} rewrite suggestions
                      </div>
                      <p className="mt-1 text-sm text-stone-600">
                        A free account unlocks the full keyword list, per-bullet rewrite
                        suggestions grounded in your real experience, and a tracker for
                        every application.
                      </p>
                      <Link to="/signup" className="mt-4 inline-block" data-testid="scan-signup-cta">
                        <Button className="rounded-sm bg-brand hover:bg-brand-hover text-white">
                          See the full analysis — free <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* The shape of the answer, with the values left as em dashes.
                 A sample score here would be a fabricated number on the one
                 page whose promise is that the numbers are real. */
              <div className="border border-sand rounded-sm bg-white overflow-hidden" data-testid="scan-preview">
                <div className="p-5 border-b border-sand">
                  <div className="u-label text-stone-600">What you get back</div>
                </div>
                <div className="p-5 flex items-center gap-5">
                  <div className="w-20 h-20 shrink-0 grid place-items-center rounded-sm border-2 border-dashed border-sand text-stone-300 font-heading text-3xl font-bold" aria-hidden="true">
                    —
                  </div>
                  <div className="text-sm text-stone-600">
                    A 0–100 match score and a plain-English verdict on how this
                    resume reads to the screener.
                  </div>
                </div>
                <ul className="px-5 pb-5 space-y-3">
                  {[
                    ["The keywords you already match", "so you know what is working"],
                    ["The keywords you are missing", "the gap, named, not guessed at"],
                    ["Where you sit on seniority", "under- or over-levelled for the posting"],
                  ].map(([t, note]) => (
                    <li key={t} className="flex gap-2.5">
                      <IconRise className="w-4 h-4 mt-0.5 text-verified shrink-0" />
                      <div>
                        <div className="text-sm text-stone-800">{t}</div>
                        <div className="text-xs text-stone-600">{note}</div>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="px-5 py-4 border-t border-sand bg-paper text-xs text-stone-600">
                  The scan reports only what is in the text you paste. It never adds
                  experience you don’t have.
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      <AtsExplainer />
    </PublicLayout>
  );
}
